import { ChoicesStatusMilestone } from '@/types/milestone.interface';
import { MilestoneStatusLabel } from '@components/core/milestone-status-label';
import { RHFNumberText } from '@components/forms/hook-form/rhf-number-field';
import Iconify from '@components/media/iconify';
import {
  Box,
  Card,
  Stack,
  Button,
  CardHeader,
  Typography,
  CardActions,
  CardContent,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { t } from 'i18next';
import moment from 'moment';
import { useMemo } from 'react';
import { useBillingStore } from '../../store/billing.store';

export const SelectedMilestonesPanel = () => {
  const theme = useTheme();

  const selectedMilestones = useBillingStore((state) => state.selectedMilestones);
  const onRemoveMilestone = useBillingStore((state) => state.deselectMilestone);
  const onClearSelection = useBillingStore((state) => state.deselectAllMilestones);

  const milestoneTotal = useMemo(() => {
    let total = 0;
    for (const milestone of selectedMilestones) {
      total += milestone.total;
    }
    return total;
  }, [selectedMilestones]);

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <CardHeader
        title={`${t('Selected Milestones')} (${selectedMilestones.length})`}
        sx={{ pb: 2 }}
      />

      <CardContent sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {selectedMilestones.length === 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
            {t('No milestones were selected')}
          </Typography>
        )}

        <Stack spacing={2}>
          {selectedMilestones.map((milestone) => (
            <Box
              key={`selected-milestone-${milestone._id}`}
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
                boxShadow: theme.customShadows?.z1,
                backgroundColor: 'background.paper',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap>
                    {`${milestone.product.name}`}
                  </Typography>
                  <Typography variant="subtitle2" noWrap>
                    {`${milestone.plan.name}`}
                  </Typography>
                </Box>
                <Iconify
                  icon="eva:close-fill"
                  onClick={() => onRemoveMilestone(milestone._id)}
                  sx={{ cursor: 'pointer', fontSize: 18, color: 'text.secondary' }}
                />
              </Stack>

              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', mb: 1, display: 'block' }}
              >
                {moment(milestone.paymentDate).utc().format('L')}
              </Typography>
              <Box sx={{ mb: 1 }}>
                <MilestoneStatusLabel status={milestone.status as ChoicesStatusMilestone} />
              </Box>

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                ${milestone.total.toLocaleString('en-US')}
              </Typography>
            </Box>
          ))}
        </Stack>
      </CardContent>

      <CardActions
        sx={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'background.paper',
          borderTop: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
          zIndex: 1,
        }}
      >
        <Stack direction="column" sx={{ width: '100%' }} spacing={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ width: '100%', fontWeight: 500 }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('Subtotal')}
            </Typography>
            <RHFNumberText value={milestoneTotal} decimalScale={2} fixedDecimalScale />
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ width: '100%', fontWeight: 500 }}
          >
            <Typography variant="h6">{t('Total')}</Typography>
            <Typography variant="h6">
              <RHFNumberText value={milestoneTotal} decimalScale={2} fixedDecimalScale />
            </Typography>
          </Stack>

          <Button variant="text" color="primary" onClick={onClearSelection}>
            <Iconify icon="solar:trash-bin-minimalistic-bold-duotone" /> {t('Clear selection')}
          </Button>
        </Stack>
      </CardActions>
    </Card>
  );
};
